import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";

import { FORM_SUMMARY } from "constants/servicePayments";
import Head from "pages/_components/Head";
import { PaymentServicesArcaConfirmation, Box, Spinner } from "@ui-kit/components/index.es";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { actions, selectors as selector } from "reducers/paymentsAFIP";
import { actions as servicePaymentsActions } from "reducers/servicePayments";
import { actions as statusActions } from "reducers/status";
import { getServicePaymentsPath } from "util/servicePaymentsRoutes";
import { selectors as sessionSelectors } from "reducers/session";
import isTokenActive from "util/token";
import {
    applyEmbeddedV3ShellLayoutState,
    clearEmbeddedV3ShellLayoutState,
    isUxDashboardImprovementsEnabled,
} from "util/v3ShellLayoutSync";
import Notification from "pages/_components/Notification";

import * as configUtils from "util/config";
import * as i18n from "util/i18n";

import { resizableRoute } from "pages/_components/Resizable";
import { getAccountDescription } from "util/accounts";
import Yup from "yup";
import { CURRENCY_CODE } from "constants.js";

function PaymentServicesArcaConfirmationPage(props) {
    const {
        dispatch,
        isDesktop,
        embeddedInV3Shell,
        debitAccount,
        afipList,
        location,
        fetchingToken,
        tokenNumber,
        userId,
    } = props;

    const newLabel = configUtils.getBoolean("frontend.Show.ContextualMenu.Functionalities", false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleBack = () => {
        dispatch(servicePaymentsActions.setPaymentSelected());
        dispatch(push(getServicePaymentsPath()));
    };

    useEffect(() => {
        if (!debitAccount?.number || afipList.length <= 0) {
            handleBack();
        }
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, []);

    useEffect(() => {
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["multipleAFIPSummary"],
            titleKey: `${FORM_SUMMARY}.confirmationTicket.title`,
            goBackPath: getServicePaymentsPath(),
            rightContentMobile: undefined,
        });
    }, [dispatch, embeddedInV3Shell, isDesktop, newLabel]);

    const locationRef = useRef(location);
    locationRef.current = location;

    useEffect(
        () => () => {
            if (embeddedInV3Shell) {
                clearEmbeddedV3ShellLayoutState(dispatch);
            }
            dispatch(statusActions.saveLastHref(locationRef.current));
        },
        [dispatch, embeddedInV3Shell],
    );

    const getToken = useCallback(async () => {
        if (!isDesktop && isTokenActive() && tokenNumber) {
            return { token: tokenNumber };
        }

        return { token: null };
    }, [isDesktop, tokenNumber]);

    const otpSchema = useMemo(
        () =>
            Yup.object({
                otp:
                    !isDesktop && isTokenActive() && tokenNumber
                        ? Yup.string().nullable()
                        : Yup.string().required(i18n.get("debin.programmed.confirmation.otp.required")),
            }),
        [isDesktop, tokenNumber],
    );

    const confirmationProps = {
        handleConfirm: ({ otp }) => {
            const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;
            const stubFormik = {
                setSubmitting: setIsSubmitting,
            };
            const afipPayments = Object.values(afipList)
                .flat()
                .map(
                    ({
                        contributorsCUIT,
                        vepNumber,
                        userCUIT,
                        amount,
                        filterType,
                        fiscalPeriod,
                        expiration,
                        concept,
                        subConcept,
                        description,
                        formNumber, // Agregar
                        advanceQuota,
                        taxpayer,
                    }) => ({
                        contributorsCUIT,
                        numberVEP: vepNumber,
                        filterType,
                        userCUIT,
                        fiscalPeriod,
                        dueDate: expiration,
                        conceptDesc: concept,
                        subConceptDesc: subConcept,
                        description,
                        formNumber,
                        advancePayment: advanceQuota,
                        taxpayerCUIT: taxpayer,
                        amount: {
                            quantity: amount,
                            currency: CURRENCY_CODE.ARS,
                        },
                    }),
                );
            const oneAFIPSelected = afipPayments.length === 1;
            setIsSubmitting(true);
            if (oneAFIPSelected) {
                dispatch(
                    actions.payPaymentAFIP(
                        { paymentAFIP: afipPayments[0], debitAccount, formikBag: stubFormik },
                        finalOtp,
                    ),
                );
            } else {
                dispatch(
                    actions.payMultipleAFIP(
                        {
                            selectedPaymentsAFIP: afipPayments,
                            debitAccountMultipleAFIP: debitAccount,
                            otp: finalOtp,
                            userId,
                        },
                        stubFormik,
                    ),
                );
            }
        },
        initialValue: {
            otp: "",
        },
        loading: isSubmitting,
        isInactive: false,
        validationConfirm: otpSchema,
        confirmationText: i18n.get(`${FORM_SUMMARY}.confirmationTicket.confirmationText`),
        getToken,
    };

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="multipleAFIPSummary" />}
            {!embeddedInV3Shell && !isDesktop && (
                <div className="admin-detail-head px-0 mb-0">
                    {isDesktop ? <Head onBack={handleBack} /> : undefined}
                    <Head
                        title={`menu.payments.payServices${newLabel ? ".arca" : "Afip"}`}
                        isFetchingExport={false}
                        onBack={!isDesktop ? handleBack : undefined}
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile servive-payment-title-mobile" : undefined
                        }
                    />
                </div>
            )}
            {fetchingToken ? (
                <Box flex justify="center" align="center" column py={72}>
                    <Spinner size="large" />
                </Box>
            ) : (
                <Box pt={isDesktop ? 0 : 20} px={isUxDashboardImprovementsEnabled() && isDesktop ? 0 : 20}>
                    <PaymentServicesArcaConfirmation
                        pageHeaderProps={{
                            goBack: handleBack,
                            title: i18n.get(`${FORM_SUMMARY}.confirmationTicket.title`),
                            renderOptionsAs: "default",
                        }}
                        headText={i18n.get(`${FORM_SUMMARY}.confirmationTicket.label`)}
                        debitAccount={!debitAccount?.number ? "" : getAccountDescription(debitAccount)}
                        vepConfirmationGroups={afipList}
                        getSubgroupTitle={(item) => i18n.get(`${FORM_SUMMARY}.confirmationTicket.${item}`)}
                        getCardLabel={(item) => i18n.get(`${FORM_SUMMARY}.confirmationTicket.card.${item}`)}
                        confirmationProps={confirmationProps}
                    />
                </Box>
            )}
        </>
    );
}

PaymentServicesArcaConfirmationPage.propTypes = {
    dispatch: PropTypes.func.isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }).isRequired,
    embeddedInV3Shell: PropTypes.bool,
    isDesktop: PropTypes.bool.isRequired,
    afipList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    debitAccount: PropTypes.shape({
        number: PropTypes.string.isRequired,
    }).isRequired,
    tokenNumber: PropTypes.string,
    fetchingToken: PropTypes.bool.isRequired,
    userId: PropTypes.string.isRequired,
};

PaymentServicesArcaConfirmationPage.defaultProps = {
    embeddedInV3Shell: false,
    tokenNumber: null,
};

const mapStateToProps = (state) => ({
    afipList: selector.getVepSelectedList(state),
    debitAccount: selector.getDebitAccountMultipleVep(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
    userId: sessionSelectors.getUser(state).userId,
});

export default compose(connect(mapStateToProps), resizableRoute)(PaymentServicesArcaConfirmationPage);
